import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { IframeService } from '@core/services/iframe.service';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SdlIdMdmIdName } from '@shared/models/SdlIdMdmIdName.model';
import { SdlIdName } from '@shared/models/sdlIdName.model';
import { DictionaryDataApiService } from '@shared/services/dictionary-data-api.service';
import { ManagedTextApiService } from '@shared/services/managed-text-api.service';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { lastValueFrom, pipe, switchMap, tap } from 'rxjs';
import { SnackbarWithLinksComponent, SnackWithLinksData } from '../components/snackbar-with-links.component';
import { AssessmentCreation, NewAssessmentResponse } from '../models/assessment.model';
import { AssessmentsApiService } from '../services/assessments-api.service';

type CreationStatus = 'idle' | 'pending' | 'success' | 'error';

type AssessmentCreationState = {
  countries: SdlIdName[];
  sectors: SdlIdMdmIdName[];
  companies: SdlIdName[];
  projects: SdlIdName[];
  tooltips: {
    countryTooltip?: string;
    sectorTooltip?: string;
    companyTooltip?: string;
    projectTooltip?: string;
    lhTooltip?: string;
    thirdPartyTooltip?: string;
  };

  isInitDataLoading: boolean;
  isCompanyLoading: boolean;
  isProjectLoading: boolean;
  creationStatus: CreationStatus;
};

const initialState: AssessmentCreationState = {
  countries: [],
  sectors: [],
  companies: [],
  projects: [],
  tooltips: {},

  isInitDataLoading: false,
  isCompanyLoading: false,
  isProjectLoading: false,
  creationStatus: 'idle',
};

const NEW_ASSESSMET_CATEGORY = 'New Assessment';

export const AssessmentCreationStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      dictionaryDataService = inject(DictionaryDataApiService),
      assessmentsApiService = inject(AssessmentsApiService),
      managedTextApiService = inject(ManagedTextApiService),
      router = inject(Router),
      iframeService = inject(IframeService),
      messageService = inject(SnackBarService)
    ) => {
      const loadInitData = async (): Promise<void> => {
        patchState(store, { isInitDataLoading: true });
        const [countries, sectors, tooltips] = await Promise.all([
          lastValueFrom(dictionaryDataService.getCountries$()),
          lastValueFrom(dictionaryDataService.getSectors$()),
          lastValueFrom(managedTextApiService.getManagedTextByCategory$(NEW_ASSESSMET_CATEGORY)),
        ]);

        const tooltipNameToDescription = Object.fromEntries(tooltips.map(t => [t.name, t.description]));
        patchState(store, {
          countries,
          sectors,
          isInitDataLoading: false,
          tooltips: {
            countryTooltip: tooltipNameToDescription['CountryTooltip'],
            sectorTooltip: tooltipNameToDescription['SectorTooltip'],
            companyTooltip: tooltipNameToDescription['CompanyTooltip'],
            projectTooltip: tooltipNameToDescription['ProjectTooltip'],
            lhTooltip: tooltipNameToDescription['LifeForAccidentTooltip'],
            thirdPartyTooltip: tooltipNameToDescription['ThirdPartyTooltip'],
          },
        });
      };

      const loadCompanies = rxMethod<number>(
        pipe(
          tap(() => patchState(store, { isCompanyLoading: true })),
          switchMap(id =>
            dictionaryDataService.getCompanies$(id).pipe(
              tapResponse({
                next: companies => patchState(store, { companies, isCompanyLoading: false }),
                error: () => {
                  patchState(store, { isCompanyLoading: false });
                },
              })
            )
          )
        )
      );

      const loadProjects = rxMethod<SdlIdName[]>(
        pipe(
          tap(() => patchState(store, { isProjectLoading: true })),
          switchMap(countries =>
            dictionaryDataService.getProjects$(countries).pipe(
              tapResponse({
                next: projects => {
                  patchState(store, { projects, isProjectLoading: false });
                },
                error: () => {
                  patchState(store, { isProjectLoading: false });
                },
              })
            )
          )
        )
      );

      const createAssessment$ = rxMethod<AssessmentCreation>(
        pipe(
          tap(() => patchState(store, { creationStatus: 'pending' })),
          switchMap(request =>
            assessmentsApiService.createAssessment$(request).pipe(
              tapResponse({
                next: response => {
                  patchState(store, { creationStatus: 'success' });
                  checkCallback(response);
                },
                error: () => patchState(store, { creationStatus: 'error' }),
              })
            )
          )
        )
      );

      const checkCallback = (response: NewAssessmentResponse) => {
        iframeService.notifyAssessmentCreated(response.id);

        const { callbackResult } = response;
        const outcome = callbackResult?.callbackOutcome;

        // If not PCBPM target or no specific outcome, just redirect
        if (callbackResult?.callbackTarget !== 'PCBPM' || !outcome) {
          redirectToDetail(response.id);
          return;
        }

        // Handle specific outcomes
        if (outcome === 'SUCCESS') {
          messageService.showInfo('Assessment created and BPM process updated.');
        } else if (outcome === 'FAILURE') {
          const snackData: SnackWithLinksData = {
            segments: [
              { type: 'text', text: ' System completed the assessment ' },
              { type: 'link', label: response.id.toString(), href: `/assessments/detail/${response.id}`, target: '_self' },
              { type: 'text', text: ' successfully. But something went wrong while updating BPM. Please try again.' },
            ],
          };
          messageService.showWithComponent(SnackbarWithLinksComponent, snackData);
          return; // Don't redirect on failure
        }

        redirectToDetail(response.id);
      };

      const redirectToDetail = (id: number) => {
        router.navigate(['/assessments/detail', id], { state: { autoGenerateInsights: true } });
      };

      return {
        loadInitData,
        loadCompanies,
        loadProjects,
        createAssessment$,
      };
    }
  ),
  withHooks({
    onInit(store) {
      store.loadInitData();
    },
  })
);
